import {
  CoverageId,
  getHasPrimaryCoverage,
  getHasSecondaryCoverage,
  InsuranceInfo,
} from '@hb/shared'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useFormState } from 'react-final-form'
import { CoverageStatus, useCoverageStatus } from './hooks'
import { useInsuranceInfoStage } from './InsuranceInfoStageContext'
import { getIsOptionDetailComplete } from './utils'

export type InsuranceInfoStep =
  | 'option'
  | 'option-detail'
  | 'primary-coverage'
  | 'secondary-coverage'
  | 'review'
export interface InsuranceInfoStepContextType {
  step: InsuranceInfoStep
  setStep: (step: InsuranceInfoStep) => void
  selectingOptionDetail: boolean
  onEditCoverage: (coverageId: CoverageId) => void
  editingCoverage: CoverageId | null
  primaryCoverage: CoverageStatus
  secondaryCoverage: CoverageStatus
}

const emptyCoverageStatus: CoverageStatus = {
  data: {},
  hasCoverage: false,
  field: { name: '', children: {} },
  type: null,
  errors: undefined,
  complete: false,
}

export const InsuranceInfoStepContext = createContext<InsuranceInfoStepContextType>({
  step: 'option',
  setStep: () => {},
  selectingOptionDetail: false,
  onEditCoverage: () => {},
  editingCoverage: null,
  primaryCoverage: emptyCoverageStatus,
  secondaryCoverage: emptyCoverageStatus,
})
const getStepFromData = (
  data: Partial<InsuranceInfo>,
  primaryComplete: boolean,
  secondaryComplete: boolean,
): InsuranceInfoStep => {
  if (!data.option) return 'option'
  if (!getIsOptionDetailComplete(data)) return 'option-detail'
  if (getHasPrimaryCoverage(data) && !primaryComplete) return 'primary-coverage'
  if (getHasSecondaryCoverage(data) && !secondaryComplete) return 'secondary-coverage'
  return 'review'
}

const canNavigateToStep = (
  targetStep: InsuranceInfoStep,
  data: Partial<InsuranceInfo>,
  hasPrimaryCoverage: boolean,
  primaryComplete: boolean,
  hasSecondaryCoverage: boolean,
): boolean => {
  if (targetStep === 'option') return true
  if (targetStep === 'option-detail') return !!data.option
  if (targetStep === 'primary-coverage')
    return !!data.option && getIsOptionDetailComplete(data) && hasPrimaryCoverage
  if (targetStep === 'secondary-coverage')
    return (
      !!data.option && getIsOptionDetailComplete(data) && primaryComplete && hasSecondaryCoverage
    )
  return false
}

export const InsuranceInfoStepProvider = ({ children }: PropsWithChildren) => {
  const { insuranceInfoData } = useInsuranceInfoStage()
  const { values } = useFormState<Partial<InsuranceInfo>>({ subscription: { values: true } })

  const primaryCoverage = useCoverageStatus('primary')
  const secondaryCoverage = useCoverageStatus('secondary')

  const selectingOptionDetail = useMemo(
    () => !!values?.option && !getIsOptionDetailComplete(values),
    [values],
  )
  const autoStep = useMemo(
    () => getStepFromData(values ?? {}, primaryCoverage.complete, secondaryCoverage.complete),
    [values, primaryCoverage.complete, secondaryCoverage.complete],
  )

  const [editingCoverage, setEditingCoverage] = useState<CoverageId | null>(null)
  useEffect(() => {
    if (primaryCoverage.hasCoverage && !primaryCoverage.complete) {
      setEditingCoverage('primary')
    } else if (secondaryCoverage.hasCoverage && !secondaryCoverage.complete) {
      setEditingCoverage('secondary')
    } else {
      setEditingCoverage(null)
    }
  }, [
    primaryCoverage.hasCoverage,
    primaryCoverage.complete,
    secondaryCoverage.hasCoverage,
    secondaryCoverage.complete,
  ])

  const onEditCoverage = useCallback(
    (coverageId: CoverageId) => {
      if (coverageId === 'primary' && primaryCoverage.hasCoverage) setEditingCoverage('primary')
      else if (coverageId === 'secondary' && secondaryCoverage.hasCoverage)
        setEditingCoverage('secondary')
      else setEditingCoverage(null)
    },
    [primaryCoverage.hasCoverage, secondaryCoverage.hasCoverage],
  )

  const [manualStep, setManualStep] = useState<InsuranceInfoStep | null>(null)

  useEffect(() => {
    if (insuranceInfoData) {
      setManualStep(null)
    }
  }, [insuranceInfoData])

  const setStep = useCallback(
    (targetStep: InsuranceInfoStep) => {
      if (
        canNavigateToStep(
          targetStep,
          values ?? {},
          primaryCoverage.hasCoverage,
          primaryCoverage.complete,
          secondaryCoverage.hasCoverage,
        )
      ) {
        setManualStep(targetStep)
      }
    },
    [values, primaryCoverage.complete, secondaryCoverage.hasCoverage, primaryCoverage.hasCoverage],
  )
  const step = manualStep ?? autoStep

  return (
    <InsuranceInfoStepContext.Provider
      value={{
        // step, setStep,
        onEditCoverage,
        editingCoverage,
        setStep,
        step,
        selectingOptionDetail,
        primaryCoverage,
        secondaryCoverage,
      }}>
      {children}
    </InsuranceInfoStepContext.Provider>
  )
}

export const useInsuranceInfoStep = () => useContext(InsuranceInfoStepContext)
